import { supabase } from './supabaseClient';

// Generate a unique referral code for a user
export const generateReferralCode = (userId) => {
  const prefix = userId.substring(0, 4).toUpperCase();
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars (0,O,1,I,L)
  let suffix = '';
  for (let i = 0; i < 4; i++) {
    suffix += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${prefix}${suffix}`;
};

// Get or create a user's referral code
export const getOrCreateReferralCode = async (userId) => {
  // First, try to get existing code
  const { data: existingCode } = await supabase
    .from('referral_codes')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (existingCode) {
    return { code: existingCode.code, isNew: false };
  }

  // Generate new code
  let code = generateReferralCode(userId);
  let attempts = 0;
  const maxAttempts = 5;

  while (attempts < maxAttempts) {
    const { data, error } = await supabase
      .from('referral_codes')
      .insert({ user_id: userId, code })
      .select()
      .single();

    if (!error) {
      return { code: data.code, isNew: true };
    }

    // If code collision, regenerate
    if (error.code === '23505') {
      code = generateReferralCode(userId);
      attempts++;
    } else {
      console.error('Error creating referral code:', error);
      throw error;
    }
  }

  throw new Error('Failed to generate unique referral code');
};

// Validate a referral/influencer code
export const validateCode = async (code) => {
  if (!code || code.length < 4) {
    return { valid: false };
  }

  const upperCode = code.toUpperCase().trim();

  // Check customer referral codes first : 
  const { data: referralCode } = await supabase
    .from('referral_codes')
    .select('*')
    .eq('code', upperCode)
    .eq('active', true)
    .single();

  if (referralCode) {
    return {
      valid: true,
      type: 'referral',
      code: upperCode,
      referrerUserId: referralCode.user_id
    };
  }

  // Check influencer codes
  const { data: influencerCode } = await supabase
    .from('influencer_codes')
    .select('*')
    .eq('code', upperCode)
    .eq('active', true)
    .single();

  if (influencerCode) {
    return {
      valid: true,
      type: 'influencer',
      code: upperCode,
      influencerCodeId: influencerCode.id,
      influencerName: influencerCode.influencer_name
    };
  }

  return { valid: false };
};

// Get referral stats for a user
export const getReferralStats = async (userId) => {
  const { data: referrals } = await supabase
    .from('referrals')
    .select('*')
    .eq('referrer_user_id', userId);

  const { data: rewards } = await supabase
    .from('referral_rewards')
    .select('*')
    .eq('user_id', userId);

  const completedReferrals = referrals?.filter(r => r.status === 'completed').length || 0;
  const pendingReferrals = referrals?.filter(r => r.status === 'pending').length || 0;

  const totalMonthsEarned = rewards?.reduce((sum, r) => sum + r.months_earned, 0) || 0;
  const monthsApplied = rewards?.reduce((sum, r) => sum + r.months_applied, 0) || 0;
  const monthsAvailable = totalMonthsEarned - monthsApplied;

  // Progress toward next reward (2 referrals = 1 month)
  const progressToNextReward = completedReferrals % 2;

  return {
    completedReferrals,
    pendingReferrals,
    totalMonthsEarned,
    monthsApplied,
    monthsAvailable,
    progressToNextReward,
    referralsNeededForNextMonth: 2 - progressToNextReward
  };
};

// Build referral URL
export const buildReferralUrl = (code) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://topswe.com';
  return `${baseUrl}/purchase?ref=${code}`;
};
